import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import { STORE_DASHBOARD_FILTER_TOGGLE_CLASS } from "../pages/storeSalesDashboardHelpers";

/**
 * @param {object} props
 * @param {string} props.label
 * @param {Array<{value: string, label: string}>} props.options
 * @param {string[]} props.value
 * @param {(next: string[]) => void} props.onChange
 * @param {string} [props.minWidth]
 */
const StoreMultiSelectFilter = ({
  label,
  options,
  value,
  onChange,
  minWidth = "160px",
}) => {
  const [open, setOpen] = useState(false);
  const selected = Array.isArray(value) ? value : [];
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggleValue = (optionValue) => {
    const next = selected.includes(optionValue)
      ? selected.filter((v) => v !== optionValue)
      : [...selected, optionValue];
    onChange(next);
  };

  const allValues = options.map((opt) => opt.value);
  const allSelected =
    options.length > 0 && allValues.every((v) => selected.includes(v));
  const someSelected = selected.length > 0 && !allSelected;

  const selectAllRef = useRef(null);
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected, open]);

  const toggleSelectAll = () => {
    onChange(allSelected ? [] : allValues);
  };

  const summary =
    selected.length === 0 ? label : `${label} (${selected.length})`;

  return (
    <div ref={rootRef} className="d-inline-block">
      <Dropdown isOpen={open} toggle={() => setOpen((v) => !v)}>
        <DropdownToggle
          tag={Button}
          size="sm"
          className={STORE_DASHBOARD_FILTER_TOGGLE_CLASS}
          style={{ minWidth, maxWidth: "220px" }}
          caret
        >
          {summary}
        </DropdownToggle>
        <DropdownMenu
          className="p-2"
          style={{ minWidth: "220px" }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {options.length === 0 ? (
            <DropdownItem disabled>No options</DropdownItem>
          ) : (
            <>
              <label
                className="dropdown-item d-flex align-items-center gap-2 mb-0 fw-semibold"
                style={{ cursor: "pointer" }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                />
                <span className="small">Select all</span>
              </label>
              <DropdownItem divider />
              {options.map((opt) => (
              <label
                key={opt.value}
                className="dropdown-item d-flex align-items-center gap-2 mb-0"
                style={{ cursor: "pointer" }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={selected.includes(opt.value)}
                  onChange={() => toggleValue(opt.value)}
                />
                <span className="small">{opt.label}</span>
              </label>
              ))}
            </>
          )}
          {selected.length > 0 ? (
            <>
              <DropdownItem divider />
              <DropdownItem
                className="small text-muted"
                onClick={(e) => {
                  e.preventDefault();
                  onChange([]);
                }}
              >
                Clear
              </DropdownItem>
            </>
          ) : null}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default StoreMultiSelectFilter;
